import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { UserLayout } from '../../components/layout/UserLayout';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { userAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { ArrowDown } from 'lucide-react';

export const SwapPage = () => {
  const location = useLocation();
  
  // Use FocusedLayout when coming from Super App
  const fromSuperApp = location.state?.fromSuperApp !== false;
  const Layout = fromSuperApp ? FocusedLayout : UserLayout;
  
  const [currencies, setCurrencies] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [fromCurrency, setFromCurrency] = useState('');
  const [toCurrency, setToCurrency] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quote, setQuote] = useState(null);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { 
    if (fromCurrency && toCurrency && amount && parseFloat(amount) > 0) {
      getQuote();
    } else {
      setQuote(null);
    }
  }, [fromCurrency, toCurrency, amount]);

  const loadData = async () => {
    try {
      const [currenciesRes, walletsRes] = await Promise.all([
        publicAPI.get('/exchange/currencies'),
        userAPI.getWallets()
      ]);
      // Filter only tradable currencies
      const tradableCurrencies = currenciesRes.data.filter(c => c.status?.trading_enabled);
      setCurrencies(tradableCurrencies);
      setWallets(walletsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const getQuote = async () => {
    if (!fromCurrency || !toCurrency || !amount) return;
    
    setQuoteLoading(true);
    try {
      const { data } = await publicAPI.get(`/exchange/quote?from_currency=${fromCurrency}&to_currency=${toCurrency}&amount=${amount}`);
      setQuote(data);
    } catch (error) {
      console.error('Quote error:', error);
      setQuote(null);
    } finally {
      setQuoteLoading(false);
    }
  };

  const fromWallet = wallets.find(w => w.currency === fromCurrency);
  const amountNum = parseFloat(amount) || 0;
  
  // Use quote data for preview
  const exchangeRate = quote?.exchange_rate || 0;
  const estimatedReceived = quote?.to_amount || 0;
  const estimatedFee = quote?.fees?.total_spread_fee || 0;

  const handleSwap = async (e) => {
    e.preventDefault();
    if (!fromCurrency || !toCurrency) { 
      toast.error('Please select both currencies'); 
      return; 
    }
    if (amountNum <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setLoading(true);
    try {
      const { data } = await userAPI.swap(fromCurrency, toCurrency, amountNum);
      toast.success(`Swap complete! Received ${data.to_amount.toFixed(8)} ${toCurrency}`);
      setAmount('');
      setQuote(null);
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Swap failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Currency Swap">
      <div className="space-y-6" data-testid="swap-page">
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Swap Currency</h1>
          <p className="mt-2 text-muted-foreground">Exchange between supported currencies</p>
        </div>

        <Card className="max-w-2xl">
          <CardHeader><CardTitle>Currency Swap</CardTitle></CardHeader>
          <CardContent>
            {currencies.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No currencies available. Contact administrator.</p>
            ) : (
              <form onSubmit={handleSwap} className="space-y-4">
                <div className="space-y-2">
                  <Label>From Currency</Label>
                  <Select value={fromCurrency} onValueChange={setFromCurrency}>
                    <SelectTrigger data-testid="from-currency-select" className="h-11">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map(c => (
                        <SelectItem key={c.currency_code} value={c.currency_code}>
                          {c.icon} {c.currency_code} - {c.name} (Balance: {wallets.find(w => w.currency === c.currency_code)?.balance.toFixed(4) || '0.00'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-center">
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                    <ArrowDown className="h-5 w-5" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>To Currency</Label>
                  <Select value={toCurrency} onValueChange={setToCurrency} disabled={!fromCurrency}>
                    <SelectTrigger data-testid="to-currency-select" className="h-11">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.filter(c => c.currency_code !== fromCurrency).map(c => (
                        <SelectItem key={c.currency_code} value={c.currency_code}>
                          {c.icon} {c.currency_code} - {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input
                    type="number" step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    data-testid="swap-amount-input"
                    className="h-11"
                    required
                  />
                  {fromWallet && <p className="text-xs text-muted-foreground">Available: {fromWallet.balance.toFixed(4)} {fromCurrency}</p>}
                </div>

                {quoteLoading && (
                  <div className="p-4 rounded-lg bg-muted/50 border text-center">
                    <p className="text-sm text-muted-foreground">Calculating best route...</p>
                  </div>
                )}

                {!quoteLoading && quote && amountNum > 0 && (
                  <div className="p-4 rounded-lg bg-muted/50 border space-y-2" data-testid="swap-preview">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Rate:</span>
                      <span className="font-medium">1 {fromCurrency} = {exchangeRate.toFixed(8)} {toCurrency}</span>
                    </div>
                    {quote.route?.path && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Route:</span>
                        <span className="font-medium text-xs">{quote.route.path}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Spread Fee:</span>
                      <span className="font-medium">{estimatedFee.toFixed(8)} {toCurrency}</span>
                    </div>
                    <div className="flex justify-between text-base font-semibold pt-2 border-t">
                      <span>You'll receive:</span>
                      <span className="text-green-600">{estimatedReceived.toFixed(8)} {toCurrency}</span>
                    </div>
                  </div>
                )}

                <Button type="submit" disabled={loading || !quote || amountNum <= 0} className="w-full h-11" data-testid="swap-submit-button">
                  {loading ? 'Swapping...' : 'Swap Now'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
